// docs/140 DT11, DT14 — what a crawl's dungeon traps leave standing on the board between pulses: a
// found plate or wire marked where it lies, a running trap's blades parked on their lanes, a cage or a
// net over the one it holds, the roller's own look over its thing. Drawn from the party's view
// (`crawl.snares`), and only where the party sees (the blind crawl fog draws nothing it has not).
import Phaser from 'phaser';
import type { Snapshot, Tile } from '../../core/sim/types';
import { PUZZLE_ATLAS } from './puzzleArt';
import { SNARE_FX, heldBy, laneSquares, rollerFrame, rollerOf, swinging, underfoot } from '../../core/snares/snares';
import { drawCage, drawNet } from './SnareFx';

export class SnareLayer {
  private objects: Phaser.GameObjects.GameObject[] = [];

  constructor(private readonly scene: Phaser.Scene, private readonly ground: Phaser.GameObjects.Layer, private readonly over: Phaser.GameObjects.Layer, private readonly cell: number) {}

  private has(frame: string): boolean { return this.scene.textures.exists(PUZZLE_ATLAS.key) && this.scene.textures.get(PUZZLE_ATLAS.key).has(frame); }
  private add<T extends Phaser.GameObjects.GameObject>(o: T, over = false): T { (over ? this.over : this.ground).add(o); this.objects.push(o); return o; }
  private centre(t: Tile): { x: number; y: number } { return { x: t.x * this.cell + this.cell / 2, y: t.y * this.cell + this.cell / 2 }; }
  private art(frame: string, t: Tile, over = false, scale = 1): Phaser.GameObjects.Image | undefined {
    if (!this.has(frame)) return undefined;
    const p = this.centre(t);
    return this.add(this.scene.add.image(p.x, p.y, PUZZLE_ATLAS.key, frame).setDisplaySize(this.cell * scale, this.cell * scale), over);
  }

  /** Draw `snap`'s traps (`hidden`: a unit's sprite id while it is drawn elsewhere, skipped). */
  draw(snap: Snapshot | undefined): void {
    for (const o of this.objects) o.destroy();
    this.objects = [];
    const crawl = snap?.crawl;
    if (!snap || !crawl?.snares.length) return;
    const fog = snap.fog;
    const sees = (t: Tile) => !fog || (fog.cells[t.y * fog.w + t.x] ?? 0) !== 0;
    const c = this.cell;
    for (const s of crawl.snares) {
      // a found plate or wire, still armed: marked where it lies
      if (s.state === 'Armed' && s.known && (s.trigger.kind === 'Step' || s.trigger.kind === 'Wire')) {
        const squares = underfoot(s).filter(sees);
        const g = this.add(this.scene.add.graphics());
        for (const t of squares) {
          if (this.art(s.trigger.kind === 'Wire' ? SNARE_FX.wire : SNARE_FX.plate, t)) continue;
          const x = t.x * c, y = t.y * c;
          if (s.trigger.kind === 'Step') {
            g.fillStyle(0x3a2a20, 0.55).fillRect(x + 3, y + 3, c - 6, c - 6);
            g.lineStyle(1, 0xffb347, 1).strokeRect(x + 3.5, y + 3.5, c - 7, c - 7);
            g.fillStyle(0xffb347, 1).fillTriangle(x + c / 2, y + c * 0.25, x + c * 0.3, y + c * 0.68, x + c * 0.7, y + c * 0.68);
            g.fillStyle(0x1a1006, 1).fillRect(x + c / 2 - 0.75, y + c * 0.38, 1.5, c * 0.16).fillRect(x + c / 2 - 0.75, y + c * 0.58, 1.5, 1.5);
          } else {
            g.lineStyle(1, 0xffb347, 1).lineBetween(x, y + c / 2, x + c, y + c / 2);
            g.fillStyle(0xffb347, 1).fillCircle(x + 2, y + c / 2, 1.5).fillCircle(x + c - 2, y + c / 2, 1.5);
          }
        }
      }
      if (s.state !== 'Running') continue;
      // the blades of a swinging hall wait on their lanes
      if (swinging(s)) {
        for (const l of s.lanes) {
          const sq = laneSquares(l);
          const mid = sq[1] ?? sq[0]!;
          if (!sees(mid)) continue;
          if (this.art(SNARE_FX.blade, mid, true, 1.1)) continue;
          const p = this.centre(mid), g = this.add(this.scene.add.graphics(), true);
          g.lineStyle(1, 0x4a4540, 0.8).lineBetween(p.x, p.y - c * 1.4, p.x, p.y);
          g.fillStyle(0xd8dce8, 0.9).slice(p.x, p.y, c * 0.4, Math.PI * 0.15, Math.PI * 0.85, false).fillPath();
        }
      }
      // whoever it holds, in a cage or a net
      for (const held of heldBy(s)) {
        for (const id of held.units) {
          const u = snap.units.find((x) => x.unitId === id);
          if (!u || u.onBoard === false || !sees(u.tile)) continue;
          if (this.art(held.act === 'Cage' ? SNARE_FX.cage : SNARE_FX.net, u.tile, true, 1.1)) continue;
          const p = this.centre(u.tile), g = this.add(this.scene.add.graphics(), true);
          g.setPosition(p.x, p.y - c * 0.1);
          if (held.act === 'Cage') drawCage(g, c); else drawNet(g, c);
        }
      }
      // the roller's own look over its thing on the field, once the art has it
      if (s.saved.thing !== undefined) {
        const thing = snap.things?.find((x) => x.id === s.saved.thing && !x.spent);
        if (thing && sees(thing.tile)) this.art(rollerFrame(rollerOf(s.def)), thing.tile, true, 1.15);
      }
    }
  }
}
